import { createSlice, createAsyncThunk, createEntityAdapter } from "@reduxjs/toolkit";
import saleApiController from "../../services/sale.service";
import { normalizeSaleInvoiceDetail, normalizeSaleInvoiceResponse } from "../../services/saleInvoiceContract";
import { recoverableWalkInSale } from "../../pages/sales/Invoice/recoveredWalkInSaleContract";
import HandleError from "../HandleError";
import HandleResponse from "../HandleResponse";
import { contentErrorMessage } from "./contentError";
import { removeServiceFromManage } from "utils/helper";
import {
  normalizeSaleMembership,
  normalizeSaleOneOffVoucher,
  normalizeSaleProduct,
  normalizeSaleProductPage,
  normalizeSaleSubscription,
  normalizeSaleVoucher,
} from "pages/sales/productMoney";
import { normalizeSelectedMembership, normalizeSelectedService } from "../../services/catalogResponse";
import { unwrapThunkResult } from "../unwrapThunkResult";
import { getCheckoutOccurrenceStart } from "../../pages/sales/Form/checkoutContract";
export const usersAdapter = createEntityAdapter();
const normalizeCost = (value) => (value != null && value !== "" ? value : undefined);
const openInvoiceErrorMessage = (action) => {
  const message = contentErrorMessage(action, "Unable to open this invoice.");
  const status = action.payload && action.payload.status;
  return status ? `${message} (HTTP ${status})` : message;
};

const listErrorMessage = (action, fallback) => contentErrorMessage(action, fallback);

// Catalogue searches are dispatched per keystroke and on cart changes, so an
// older response must not replace the results or status of the latest query.
const beginSaleCatalogRequest = (state, action, key) => {
  state[key] = action.meta?.requestId || "";
};

const isLatestSaleCatalogRequest = (state, action, key) => (
  !action.meta?.requestId
  || state[key] === action.meta.requestId
);

const currentDraftVersion = (getState) => {
  const state = getState();
  return (state.sale || state).draftVersion || 0;
};

// These thunks only read selections/benefits. A result from an abandoned draft
// must also reject at the caller boundary, where success handlers open drawers.
const createDraftReadThunk = (type, read) => createAsyncThunk(type, async (values, thunkAPI) => {
  const version = currentDraftVersion(thunkAPI.getState);
  try {
    const result = await read(values, thunkAPI);
    return currentDraftVersion(thunkAPI.getState) === version
      ? result
      : thunkAPI.rejectWithValue({ code: "stale_invoice_draft" });
  } catch (error) {
    if (currentDraftVersion(thunkAPI.getState) !== version) {
      return thunkAPI.rejectWithValue({ code: "stale_invoice_draft" });
    }
    throw error;
  }
});

export const saleStoreApi = createAsyncThunk("sale/create", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .create(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "create"))
      .catch((error) => HandleError(thunkAPI, error, "create"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const saleUpdateApi = createAsyncThunk("sale/update", async (formvalues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .update(formvalues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "update"))
      .catch((error) => HandleError(thunkAPI, error, "update"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue({ status: error.response.status, message: message });
  }
});

export const saleVoidInvoiceApi = createAsyncThunk("sale/voidInvoice", async (formvalues, thunkAPI) => {
  try {
    const response = await saleApiController.voidInvoice(formvalues);
    return HandleResponse(thunkAPI, response, "voidInvoice");
  } catch (error) {
    return HandleError(thunkAPI, error, "voidInvoice");
  }
});

export const InvoiceListViewApi = createAsyncThunk("sale/invoicelistview", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .invoiceview(formValues, thunkAPI)
      .then((response) =>
        HandleResponse(
          thunkAPI,
          { ...response, data: normalizeSaleInvoiceResponse(response.data) },
          "invoicelistview",
        ),
      )
      .catch((error) => HandleError(thunkAPI, error, "invoicelistview"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const CreateInvoiceListViewApi = createAsyncThunk("sale/createinvoicelistview", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .createinvoiceview(formValues, thunkAPI)
      .then((response) =>
        HandleResponse(
          thunkAPI,
          { ...response, data: normalizeSaleInvoiceResponse(response.data) },
          "createinvoicelistview",
        ),
      )
      .catch((error) => HandleError(thunkAPI, error, "createinvoicelistview"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const saleDetailApi = createAsyncThunk("sale/detail", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .view(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "detail"))
      .catch((error) => HandleError(thunkAPI, error, "detail"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const saleDeleteApi = createAsyncThunk("sale/delete", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .deleted(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "delete"))
      .catch((error) => HandleError(thunkAPI, error, "delete"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleServiceApi = createAsyncThunk("sale/services", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .services(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "services"))
      .catch((error) => HandleError(thunkAPI, error, "services"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleProductApi = createAsyncThunk("sale/products", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .products(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "products"))
      .catch((error) => HandleError(thunkAPI, error, "products"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleProductStaffDropdownApi = createAsyncThunk("sale/productsstafflists", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .productstafflist(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "saleproductsstafflists"))
      .catch((error) => HandleError(thunkAPI, error, "saleproductsstafflists"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleSubscriptionStaffDropdownApi = createAsyncThunk("sale/subscriptionsstafflists", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .subscriptionstafflist(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "salesubscriptionsstafflists"))
      .catch((error) => HandleError(thunkAPI, error, "salesubscriptionsstafflists"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleVoucherApi = createAsyncThunk("sale/vouchers", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .vouchers(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "vouchers"))
      .catch((error) => HandleError(thunkAPI, error, "vouchers"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleMembershipApi = createAsyncThunk("sale/membership", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .membership(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "membership"))
      .catch((error) => HandleError(thunkAPI, error, "membership"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleSubscriptionApi = createAsyncThunk("sale/subscription", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .subscription(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "subscription"))
      .catch((error) => HandleError(thunkAPI, error, "subscription"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleServiceToCartApi = createDraftReadThunk("sale/servicetocart", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .services(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "servicetocart"))
      .catch((error) => HandleError(thunkAPI, error, "servicetocart"));
    const selectedService = normalizeSelectedService(
      unwrapThunkResult(resposedata),
      formValues && formValues.service_id,
    );
    return selectedService || thunkAPI.rejectWithValue({ message: "Selected service was not returned." });
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleProductToCartApi = createDraftReadThunk("sale/producttocart", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .products(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "producttocart"))
      .catch((error) => HandleError(thunkAPI, error, "producttocart"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleVoucherToCartApi = createDraftReadThunk("sale/vouchertocart", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .vouchers(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "vouchertocart"))
      .catch((error) => HandleError(thunkAPI, error, "vouchertocart"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleMembershipToCartApi = createDraftReadThunk("sale/membershiptocart", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .membership(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "membershiptocart"))
      .catch((error) => HandleError(thunkAPI, error, "membershiptocart"));
    const selectedMembership = normalizeSelectedMembership(
      unwrapThunkResult(resposedata),
      formValues && formValues.membership_id,
    );
    return selectedMembership || thunkAPI.rejectWithValue({ message: "Selected package was not returned." });
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleSubscriptionToCartApi = createDraftReadThunk("sale/subscriptiontocart", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .subscription(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "subscriptiontocart"))
      .catch((error) => HandleError(thunkAPI, error, "subscriptiontocart"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const ClientSuggetionListApi = createDraftReadThunk("sale/clientsuggetionlist", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .clientsuggetionlist(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "suggetionlist"))
      .catch((error) => HandleError(thunkAPI, error, "suggetionlist"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleEmailInvoiceApi = createAsyncThunk("sale/saleemailinvoice", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .sendEmailInvoice(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "saleemailinvoice"))
      .catch((error) => HandleError(thunkAPI, error, "saleemailinvoice"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const VoucherApplyApi = createDraftReadThunk("sale/voucherapply", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .voucherapply(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "voucherapply"))
      .catch((error) => HandleError(thunkAPI, error, "voucherapply"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const ReturnPaymentApi = createAsyncThunk("sale/returnpayment", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .returnpayment(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "returnpayment"))
      .catch((error) => HandleError(thunkAPI, error, "returnpayment"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SendEmailVoucherApi = createAsyncThunk("sale/sendemailvoucher", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .sendemailvoucher(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "sendemailvoucher"))
      .catch((error) => HandleError(thunkAPI, error, "sendemailvoucher"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const SaleServicesManageApi = createDraftReadThunk("sale/saleservicesmanage", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .saleservicesmanage(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "saleservicesmanage"))
      .catch((error) => HandleError(thunkAPI, error, "saleservicesmanage"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const ViewSaleDetailInvoiceApi = createDraftReadThunk("sale/sale-detail", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .saledetails(formValues, thunkAPI)
      .then((response) => {
        if (response && response.status >= 200 && response.status < 300) {
          return thunkAPI.fulfillWithValue(normalizeSaleInvoiceDetail(response.data));
        }
        return thunkAPI.rejectWithValue();
      })
      .catch((error) => HandleError(thunkAPI, error, "sale-detail"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

export const saleInvoiceToCartApi = createDraftReadThunk("sale/invoicetocart", async (formValues, thunkAPI) => {
  try {
    const resposedata = await saleApiController
      .createinvoiceview(formValues, thunkAPI)
      .then((response) => HandleResponse(thunkAPI, response, "invoicetocart"))
      .catch((error) => HandleError(thunkAPI, error, "invoicetocart"));
    return resposedata;
  } catch (error) {
    const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
    return thunkAPI.rejectWithValue(message);
  }
});

const initialState = {
  draftVersion: 0,
  isRecoveredSale: null,
  draftRequests: {},
  isOpenedAddForm: "",
  isOpenedVoucherToForm: "",
  isOpenedDetailModal: "",
  isTabView: "viewinvoice",
  isSaleTabView: "services",
  isInvoiceListView: [],
  isCreateInvoiceListView: [],
  isInvoiceListViewError: "",
  isCreateInvoiceListViewError: "",
  latestInvoiceListRequestId: "",
  latestCreateInvoiceListRequestId: "",
  latestServiceListRequestId: "",
  latestProductListRequestId: "",
  latestVoucherListRequestId: "",
  latestMembershipListRequestId: "",
  latestSubscriptionListRequestId: "",
  isDetailData: "",
  isServices: [],
  isServiceSearch: "",
  isServiceSearchName: "",
  isProducts: [],
  isProductSearch: "",
  isProductSearchName: "",
  isCart: { appointments: [], services: [], products: [], vouchers: [], membership: [], oneoffvoucher: [], subscription: [] },
  isCartTotalPrice: [],
  isAppointmentDetail: "",
  isSuggetionListView: [],
  isSearchList: "",
  isSearchName: "",
  isSearchObj: "",
  isVouchers: [],
  isMembership: [],
  isSubscription: [],
  isVoucherToFormData: [],
  isCartVoucherCount: [],
  isOpenedCheckoutForm: "",
  isOpenSubscriptionCheckoutForm: "",
  isCheckoutData: [],
  isOpenedSaleCompleted: "",
  isSaleCompletedData: "",
  isOpenCardPaymentForm: "",
  isCardPaymentData: "",
  isOpenedVoucherApplyForm: "",
  isApplyVoucherCodeData: "",
  isSaleServicesManage: "",
  isSendEmailVoucher: "",
  isViewSaleDetailInvoice: "",
  isSaleProductStaffDropdownList: [],
  isSaleSubscriptionStaffDropdownList: [],
  isApiStatus: {
    saleStoreApi: "",
    saleUpdateApi: "",
    InvoiceListViewApi: "",
    CreateInvoiceListViewApi: "",
    saleDetailApi: "",
    saleDeleteApi: "",
    SaleServiceApi: "",
    SaleProductApi: "",
    SaleVoucherApi: "",
    SaleMembershipApi: "",
    SaleSubscriptionApi: "",
    SaleServiceToCartApi: "",
    SaleProductToCartApi: "",
    SaleVoucherToCartApi: "",
    SaleMembershipToCartApi: "",
    SaleSubscriptionToCartApi: "",
    ClientSuggetionListApi: "",
    SaleEmailInvoiceApi: "",
    VoucherApplyApi: "",
    ReturnPaymentApi: "",
    SendEmailVoucherApi: "",
    SaleServicesManageApi: "",
    SaleProductStaffDropdownApi: "",
    saleInvoiceToCartApi: "",
    ViewSaleDetailInvoiceApi: ""
  },
};

const closeCheckoutOverlays = (state) => {
  state.isOpenCardPaymentForm = "";
  state.isOpenedVoucherApplyForm = "";
  state.isOpenedVoucherToForm = "";
  state.isOpenSubscriptionCheckoutForm = "";
};

// Clear only the working draft; completed receipt and invoice lists stay available.
const clearInvoiceDraft = (state) => {
  state.draftVersion = (state.draftVersion || 0) + 1;
  state.draftRequests = {};
  for (const key of draftReadStatuses) state.isApiStatus[key] = "";
  state.isCreateInvoiceListViewError = "";
  for (const field of [
    'isCart', 'isCartTotalPrice', 'isAppointmentDetail', 'isCheckoutData', 'isRecoveredSale',
    'isSearchName', 'isSearchObj', 'isSearchList', 'isSuggetionListView',
    'isVoucherToFormData', 'isCartVoucherCount', 'isCardPaymentData',
    'isApplyVoucherCodeData', 'isSaleServicesManage', 'isSaleTabView',
    'isServiceSearchName', 'isProductSearchName', 'isOpenedAddForm', 'isOpenedCheckoutForm',
  ]) state[field] = initialState[field];
  closeCheckoutOverlays(state);
};

const draftReadStatuses = [
  "SaleServiceToCartApi", "SaleProductToCartApi", "SaleVoucherToCartApi",
  "SaleMembershipToCartApi", "SaleSubscriptionToCartApi", "VoucherApplyApi",
  "ViewSaleDetailInvoiceApi", "saleInvoiceToCartApi", "SaleServicesManageApi",
  "ClientSuggetionListApi",
];

const registerDraftRequest = (state, action) => {
  if (!state.draftRequests) state.draftRequests = {};
  state.draftRequests[action.meta.requestId] = state.draftVersion || 0;
};

const consumeDraftRequest = (state, action) => {
  if (state.draftRequests?.[action.meta.requestId] !== (state.draftVersion || 0)) return false;
  delete state.draftRequests[action.meta.requestId];
  return true;
};

const saleSlice = createSlice({
  name: "sale",
  initialState,
  reducers: {
    reset: (state) => ({ ...initialState, draftVersion: (state.draftVersion || 0) + 1 }),
    StartNewInvoice: (state) => {
      clearInvoiceDraft(state);
      state.isViewSaleDetailInvoice = '';
      state.isSaleCompletedData = '';
      state.isOpenedSaleCompleted = '';
    },
    InvoiceTabView: (state, action) => {
      state.isTabView = action.payload;
    },
    SaleTabView: (state, action) => {
      state.isSaleTabView = action.payload;
    },
    openAddSaleForm: (state = initialState) => {
      // state.isOpenedEditForm = "";
      state.isOpenedAddForm = "open";
    },
    closeAddSaleForm: (state = initialState) => {
      closeCheckoutOverlays(state);
      state.isOpenedCheckoutForm = "";
      // state.isOpenedEditForm = "";
      state.isOpenedAddForm = "";
    },
    OpenVoucherToForm: (state = initialState) => {
      // state.isOpenedEditForm = "";
      state.isOpenedVoucherToForm = "open";
    },
    CloseVoucherToForm: (state = initialState) => {
      // state.isOpenedEditForm = "";
      state.isOpenedVoucherToForm = "";
    },
    OpenVoucherApplyForm: (state = initialState) => {
      // state.isOpenedEditForm = "";
      state.isOpenedVoucherApplyForm = "open";
    },
    CloseVoucherApplyForm: (state = initialState) => {
      // state.isOpenedEditForm = "";
      state.isOpenedVoucherApplyForm = "";
    },
    VoucherToFormData: (state, action) => {
      state.isVoucherToFormData = action.payload;
    },
    openSaleDetailModal: (state = initialState) => {
      state.isOpenedAddForm = "";
      state.isOpenedDetailModal = "open";
    },
    closeSaleDetailModal: (state = initialState) => {
      state.isOpenedAddForm = "";
      state.isOpenedDetailModal = "";
    },
    SaleProductSearchName: (state, action) => {
      state.isProductSearchName = action.payload;
    },
    SaleServiceSearchName: (state, action) => {
      state.isServiceSearchName = action.payload;
    },
    SaleServiceRemoveToCart: (state, action) => {
      const { id } = action.payload;
      state.isCart.services = state.isCart.services ? state.isCart.services.filter((item) => item.id != id) : [];
    },
    SaleProductRemoveToCart: (state, action) => {
      const { id } = action.payload;
      state.isCart.products = state.isCart.products ? state.isCart.products.filter((item) => item.id != id) : [];
    },
    SaleVoucherRemoveToCart: (state, action) => {
      const { id } = action.payload;
      state.isCart.vouchers = state.isCart.vouchers ? state.isCart.vouchers.filter((item) => item.id != id) : [];
    },
    SaleOneOffVoucherRemoveToCart: (state, action) => {
      const { id } = action.payload;
      state.isCart.oneoffvoucher = state.isCart.oneoffvoucher ? state.isCart.oneoffvoucher.filter((item) => item.id != id) : [];
      // state.isCart.oneoffvoucher = state.isCart.oneoffvoucher ? state.isCart.oneoffvoucher.slice(0, i).concat(state.isCart.oneoffvoucher.slice(i + 1, state.isCart.oneoffvoucher.length)) : [];
    },
    SaleMembershipRemoveToCart: (state, action) => {
      const { id } = action.payload;
      state.isCart.membership = state.isCart.membership ? state.isCart.membership.filter((item) => item.id != id) : [];
    },
    SaleSubscriptionRemoveToCart: (state, action) => {
      const { id } = action.payload;
      state.isCart.subscription = state.isCart.subscription ? state.isCart.subscription.filter((item) => item.id != id) : [];
    },
    AppointmentDetail: (state, action) => {
      state.isRecoveredSale = null;
      state.isAppointmentDetail = action.payload;
    },
    ResolvedRecurringCheckout: (state, action) => {
      const { source_appointment_id, source_sale_id, occurrence_start_datetime, appointment, cart } = action.payload;
      const selected = state.isAppointmentDetail || state.isCart?.appointments?.[0];
      if (String(selected?.id) !== String(source_appointment_id)
          || String(selected?.sale_id) !== String(source_sale_id)
          || getCheckoutOccurrenceStart(selected) !== (occurrence_start_datetime || "")
          || !state.isOpenedCheckoutForm) return;
      // A successful split changes invoice ownership within the open drawer.
      // Reuse the existing draft boundary so older reads reject before their
      // callbacks can hydrate the source invoice or its shared appointments.
      state.draftVersion = (state.draftVersion || 0) + 1;
      state.draftRequests = {};
      for (const key of draftReadStatuses) state.isApiStatus[key] = "";
      state.isAppointmentDetail = appointment;
      state.isCart = cart;
      state.isCheckoutData = { ...state.isCheckoutData, client: appointment.client };
    },
    OpenClientSearchList: (state) => {
      state.isSearchList = "open";
    },
    CloseClientSearchList: (state) => {
      state.isSearchList = "";
    },
    ClientSearchName: (state, action) => {
      state.isSearchName = action.payload;
    },
    ClientSearchObj: (state, action) => {
      state.isSearchObj = action.payload;
    },
    SaleOneOffVoucherToCartApi: (state, action) => {
      const oneOffVoucherPayload = normalizeSaleOneOffVoucher(action.payload);
      const { id, ...changes } = oneOffVoucherPayload;
      const existingData = state.isCart.oneoffvoucher.find((event) => event.id === id);
      if (existingData) {
        Object.keys(changes).map((keyName) => {
          existingData[keyName] = changes[keyName];
        });
      } else {
        const oneoffvoucher = [
          ...state.isCart.oneoffvoucher,
          { ...oneOffVoucherPayload, id: state.isCart.oneoffvoucher.length + 1 },
        ];
        state.isCart.oneoffvoucher = oneoffvoucher;
      }
    },
    SaleAppointmentToCartApi: (state, action) => {
      const { id, ...changes } = action.payload;
      // The cart row id must stay the appointment's database id. Checkout sends
      // it to sale/benefitoptions, which validates it against the salon's
      // appointment rows, and removeStaleCartAppointments matches it against the
      // open appointment. Numbering rows by cart position made those calls look
      // up an appointment that does not exist.
      const appointmentId = Number(id);
      const cartId = Number.isInteger(appointmentId) && appointmentId > 0
        ? appointmentId
        : state.isCart.appointments.length + 1;
      const existingData = state.isCart.appointments.find((event) => Number(event.id) === cartId);
      if (existingData) {
        Object.keys(changes).map((keyName) => {
          existingData[keyName] = changes[keyName];
        });
      } else {
        const appointments = [...state.isCart.appointments, { ...action.payload, id: cartId }];
        state.isCart.appointments = appointments;
      }
    },
    RecoverPendingWalkInSale: (state, action) => {
      const invoice = recoverableWalkInSale(action.payload);
      if (!invoice) return;
      clearInvoiceDraft(state);
      state.isRecoveredSale = invoice;
      state.isCheckoutData = { client: invoice.client };
      state.isOpenedAddForm = "open";
    },
    SaleCheckoutData: (state, action) => {
      state.isCheckoutData = action.payload;
      //console.log('action.payload: ', action.payload);
      // state.isCart = action.payload;
    },
    OpenCheckoutForm: (state = initialState) => {
      // state.isOpenedEditForm = "";
      closeCheckoutOverlays(state);
      state.isOpenedCheckoutForm = "open";
    },
    CloseCheckoutForm: (state = initialState) => {
      // state.isOpenedEditForm = "";
      closeCheckoutOverlays(state);
      state.isOpenedCheckoutForm = "";
    },
    OpenSubscriptionCheckoutForm: (state, action) => {
      state.isOpenSubscriptionCheckoutForm = action.payload;
    },
    SaleCartUpdate: (state, action) => {
      let cartupdate = action.payload;
      if (cartupdate) {
        const { id, ...changes } = action.payload;
        let existingData = "";
        if (cartupdate.type === "products") {
          existingData = state.isCart.products.find((event) => event.id === id);
        }
        if (cartupdate.type === "services") {
          existingData = state.isCart.services.find((event) => event.id === id);
        }
        if (cartupdate.type === "subscription") {
          existingData = state.isCart.subscription.find((event) => event.id === id);
        }
        if (cartupdate.type === "membership") {
          existingData = state.isCart.membership.find((event) => event.id === id);
        }
        if (cartupdate.type === "appointments") {
          existingData = state.isCart.appointments.find((event) => event.id === id);
        }
        if (cartupdate.type === "appointments_addon") {
          existingData = state.isCart.appointments[0].addonservices.find((event) => event.id === id);
        }
        if (cartupdate.type === "appointments_addanother") {
          existingData = state.isCart.appointments[0].addanotherservices.find((event) => event.id === id);
        }
        if (existingData) {
          Object.keys(changes).map((keyName) => {
            existingData[keyName] = changes[keyName];
          });
        }
      }
    },
    OpenSaleCompleted: (state = initialState) => {
      clearInvoiceDraft(state);
      state.isOpenedCheckoutForm = "";
      state.isOpenedSaleCompleted = "open";
    },
    CloseSaleCompleted: (state = initialState) => {
      state.isOpenedSaleCompleted = "";
    },
    SaleCompletedData: (state, action) => {
      state.isSaleCompletedData = action.payload;
    },
    OpenCardPaymentForm: (state = initialState) => {
      // state.isOpenedEditForm = "";
      state.isOpenCardPaymentForm = "open";
    },
    CloseCardPaymentForm: (state = initialState) => {
      // state.isOpenedEditForm = "";
      state.isOpenCardPaymentForm = "";
    },
    CardPaymentData: (state, action) => {
      state.isCardPaymentData = action.payload;
    },
    RemoveApplyVoucherCode: (state) => {
      state.isApplyVoucherCodeData = "";
    },
    RemoveSaleServicesManage: (state, action) => {
      const { serviceId, type } = action.payload;

      state.isSaleServicesManage = removeServiceFromManage(
          state.isSaleServicesManage,
          serviceId,
          type
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ViewSaleDetailInvoiceApi.pending, (state, action) => {
        registerDraftRequest(state, action);
        state.isApiStatus.ViewSaleDetailInvoiceApi = 'loading';
      })
      .addCase(ViewSaleDetailInvoiceApi.fulfilled, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isViewSaleDetailInvoice = action.payload;
        state.isApiStatus.ViewSaleDetailInvoiceApi = 'succeeded';
      })
      .addCase(ViewSaleDetailInvoiceApi.rejected, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isViewSaleDetailInvoice = "";
        state.isApiStatus.ViewSaleDetailInvoiceApi = 'failed';
      })
      .addCase(saleInvoiceToCartApi.pending, (state, action) => {
        registerDraftRequest(state, action);
        state.isApiStatus.saleInvoiceToCartApi = 'loading';
        state.isCreateInvoiceListViewError = "";
      })
      .addCase(saleInvoiceToCartApi.fulfilled, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isApiStatus.saleInvoiceToCartApi = 'succeeded';
        state.isCreateInvoiceListViewError = "";
      })
      .addCase(saleInvoiceToCartApi.rejected, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isApiStatus.saleInvoiceToCartApi = 'failed';
        // The row click used to stop its spinner and show nothing, so a 500 from
        // the pending-invoice read was invisible to the user.
        state.isCreateInvoiceListViewError = openInvoiceErrorMessage(action);
      })
      .addCase(saleStoreApi.pending, (state) => {
        state.isApiStatus.saleStoreApi = 'loading';
      })
      .addCase(saleStoreApi.fulfilled, (state, action) => {
        if (state.isInvoiceListView && state.isInvoiceListView.data) {
          state.isInvoiceListView.data = [action.payload, ...state.isInvoiceListView.data];
        } else {
          state.isInvoiceListView = { data: [action.payload] };
        }
        state.isApiStatus.saleStoreApi = 'succeeded';
      })
      .addCase(saleStoreApi.rejected, (state) => {
        state.isApiStatus.saleStoreApi = 'failed';
      })
      .addCase(saleUpdateApi.pending, (state) => {
        state.isApiStatus.saleUpdateApi = 'loading';
      })
      .addCase(saleUpdateApi.fulfilled, (state, action) => {
        const { id, ...changes } = action.payload;
        const list = state.isInvoiceListView?.data;
        const existingData = Array.isArray(list) ? list.find((event) => event.id === id) : null;
        if (existingData) {
          Object.assign(existingData, changes);
          if (changes.invoicedate) existingData.invoice_date = changes.invoicedate;
        }
        if (state.isViewSaleDetailInvoice?.id === id && changes.invoicedate) {
          state.isViewSaleDetailInvoice.invoicedate = changes.invoicedate;
          state.isViewSaleDetailInvoice.invoice_date = changes.invoicedate;
        }
        state.isApiStatus.saleUpdateApi = 'succeeded';
      })
      .addCase(saleUpdateApi.rejected, (state) => {
        state.isApiStatus.saleUpdateApi = 'failed';
      })
      .addCase(InvoiceListViewApi.pending, (state, action) => {
        state.latestInvoiceListRequestId = action.meta.requestId;
        state.isInvoiceListViewError = "";
        state.isApiStatus.InvoiceListViewApi = 'loading';
      })
      .addCase(InvoiceListViewApi.fulfilled, (state, action) => {
        if (state.latestInvoiceListRequestId !== action.meta.requestId) {
          return;
        }
        const oldCurrentPage = state.isInvoiceListView.current_page || "";
        const newCurrentPage = action.payload.current_page || "";
        const viewData = state.isInvoiceListView && state.isInvoiceListView.data;
        const newViewData = action.payload && action.payload.data;
        state.isInvoiceListView = action.payload;
        if (oldCurrentPage && newCurrentPage && oldCurrentPage < newCurrentPage && oldCurrentPage !== newCurrentPage) {
          state.isInvoiceListView.data = viewData && newViewData ? [...viewData, ...newViewData] : action.payload;
        }
        state.isApiStatus.InvoiceListViewApi = 'succeeded';
        state.isInvoiceListViewError = "";
      })
      .addCase(InvoiceListViewApi.rejected, (state, action) => {
        if (state.latestInvoiceListRequestId !== action.meta.requestId) {
          return;
        }
        state.isApiStatus.InvoiceListViewApi = 'failed';
        state.isInvoiceListViewError = listErrorMessage(action, "Unable to load invoices.");
      })
      .addCase(CreateInvoiceListViewApi.pending, (state, action) => {
        state.latestCreateInvoiceListRequestId = action.meta.requestId;
        state.isCreateInvoiceListViewError = "";
        state.isApiStatus.CreateInvoiceListViewApi = 'loading';
      })
      .addCase(CreateInvoiceListViewApi.fulfilled, (state, action) => {
        if (state.latestCreateInvoiceListRequestId !== action.meta.requestId) {
          return;
        }
        const oldCurrentPage = state.isCreateInvoiceListView.current_page || "";
        const newCurrentPage = action.payload.current_page || "";
        const viewData = state.isCreateInvoiceListView && state.isCreateInvoiceListView.data;
        const newViewData = action.payload && action.payload.data;
        state.isCreateInvoiceListView = action.payload;
        if (oldCurrentPage && newCurrentPage && oldCurrentPage < newCurrentPage && oldCurrentPage !== newCurrentPage) {
          state.isCreateInvoiceListView.data = viewData && newViewData ? [...viewData, ...newViewData] : action.payload;
        }
        state.isApiStatus.CreateInvoiceListViewApi = 'succeeded';
        state.isCreateInvoiceListViewError = "";
      })
      .addCase(CreateInvoiceListViewApi.rejected, (state, action) => {
        if (state.latestCreateInvoiceListRequestId !== action.meta.requestId) {
          return;
        }
        state.isApiStatus.CreateInvoiceListViewApi = 'failed';
        state.isCreateInvoiceListViewError = listErrorMessage(action, "Unable to load create-invoice rows.");
      })
      .addCase(saleDetailApi.pending, (state) => {
        state.isApiStatus.saleDetailApi = 'loading';
      })
      .addCase(saleDetailApi.fulfilled, (state, action) => {
        state.isApiStatus.saleDetailApi = 'succeeded';
        state.isDetailData = action.payload;
      })
      .addCase(saleDetailApi.rejected, (state) => {
        state.isApiStatus.saleDeleteApi = 'failed';
        state.isDetailData = "";
      })
      .addCase(saleDeleteApi.pending, (state) => {
        state.isApiStatus.saleDeleteApi = 'loading';
      })
      .addCase(saleDeleteApi.fulfilled, (state, action) => {
        const { id } = action.payload;
        state.isApiStatus.saleDeleteApi = 'succeeded';
        state.isInvoiceListView.data = state.isInvoiceListView.data ? state.isInvoiceListView.data.filter((item) => item.id !== id) : state.isInvoiceListView.filter((item) => item.id !== id);
      })
      .addCase(saleDeleteApi.rejected, (state) => {
        state.isApiStatus.saleDeleteApi = 'failed';
      })
      .addCase(SaleServiceApi.pending, (state, action) => {
        beginSaleCatalogRequest(state, action, "latestServiceListRequestId");
        state.isApiStatus.SaleServiceApi = 'loading';
      })
      .addCase(SaleServiceApi.fulfilled, (state, action) => {
        if (!isLatestSaleCatalogRequest(state, action, "latestServiceListRequestId")) return;
        const oldCurrentPage = state.isServices.current_page || "";
        const newCurrentPage = action.payload.current_page || "";
        const viewData = state.isServices && state.isServices.data;
        const newViewData = action.payload && action.payload.data;
        state.isServices = action.payload;
        if (oldCurrentPage && newCurrentPage && oldCurrentPage < newCurrentPage && oldCurrentPage !== newCurrentPage) {
          state.isServices.data = viewData && newViewData ? [...viewData, ...newViewData] : action.payload;
        }
        state.isApiStatus.SaleServiceApi = 'succeeded';
      })
      .addCase(SaleServiceApi.rejected, (state, action) => {
        if (!isLatestSaleCatalogRequest(state, action, "latestServiceListRequestId")) return;
        state.isServices = [];
        state.isApiStatus.SaleServiceApi = 'failed';
      })
      .addCase(SaleProductApi.pending, (state, action) => {
        beginSaleCatalogRequest(state, action, "latestProductListRequestId");
        state.isApiStatus.SaleProductApi = 'loading';
      })
      .addCase(SaleProductApi.fulfilled, (state, action) => {
        if (!isLatestSaleCatalogRequest(state, action, "latestProductListRequestId")) return;
        const oldCurrentPage = state.isProducts.current_page || "";
        const newCurrentPage = action.payload.current_page || "";
        const viewData = state.isProducts && state.isProducts.data;
        const normalizedPayload = normalizeSaleProductPage(action.payload);
        const newViewData = normalizedPayload && normalizedPayload.data;
        state.isProducts = normalizedPayload;
        if (oldCurrentPage && newCurrentPage && oldCurrentPage < newCurrentPage && oldCurrentPage !== newCurrentPage) {
          state.isProducts.data = viewData && newViewData ? [...viewData, ...newViewData] : normalizedPayload;
        }
        state.isApiStatus.SaleProductApi = 'succeeded';
      })
      .addCase(SaleProductApi.rejected, (state, action) => {
        if (!isLatestSaleCatalogRequest(state, action, "latestProductListRequestId")) return;
        state.isProducts = [];
        state.isApiStatus.SaleProductApi = 'failed';
      })
      .addCase(SaleVoucherApi.pending, (state, action) => {
        beginSaleCatalogRequest(state, action, "latestVoucherListRequestId");
        state.isApiStatus.SaleVoucherApi = 'loading';
      })
      .addCase(SaleVoucherApi.fulfilled, (state, action) => {
        if (!isLatestSaleCatalogRequest(state, action, "latestVoucherListRequestId")) return;
        const oldCurrentPage = state.isVouchers.current_page || "";
        const newCurrentPage = action.payload.current_page || "";
        const viewData = state.isVouchers && state.isVouchers.data;
        const newViewData = action.payload && action.payload.data;
        state.isVouchers = action.payload;
        if (oldCurrentPage && newCurrentPage && oldCurrentPage < newCurrentPage && oldCurrentPage !== newCurrentPage) {
          state.isVouchers.data = viewData && newViewData ? [...viewData, ...newViewData] : action.payload;
        }
        state.isApiStatus.SaleVoucherApi = 'succeeded';
      })
      .addCase(SaleVoucherApi.rejected, (state, action) => {
        if (!isLatestSaleCatalogRequest(state, action, "latestVoucherListRequestId")) return;
        state.isVouchers = [];
        state.isApiStatus.SaleVoucherApi = 'failed';
      })
      .addCase(SaleMembershipApi.pending, (state, action) => {
        beginSaleCatalogRequest(state, action, "latestMembershipListRequestId");
        state.isApiStatus.SaleMembershipApi = 'loading';
      })
      .addCase(SaleMembershipApi.fulfilled, (state, action) => {
        if (!isLatestSaleCatalogRequest(state, action, "latestMembershipListRequestId")) return;
        const oldCurrentPage = state.isMembership.current_page || "";
        const newCurrentPage = action.payload.current_page || "";
        const viewData = state.isMembership && state.isMembership.data;
        const newViewData = action.payload && action.payload.data;
        state.isMembership = action.payload;
        if (oldCurrentPage && newCurrentPage && oldCurrentPage < newCurrentPage && oldCurrentPage !== newCurrentPage) {
          state.isMembership.data = viewData && newViewData ? [...viewData, ...newViewData] : action.payload;
        }
        state.isApiStatus.SaleMembershipApi = 'succeeded';
      })
      .addCase(SaleMembershipApi.rejected, (state, action) => {
        if (!isLatestSaleCatalogRequest(state, action, "latestMembershipListRequestId")) return;
        state.isMembership = [];
        state.isApiStatus.SaleMembershipApi = 'failed';
      })
      .addCase(SaleSubscriptionApi.pending, (state, action) => {
        beginSaleCatalogRequest(state, action, "latestSubscriptionListRequestId");
        state.isApiStatus.SaleSubscriptionApi = 'loading';
      })
      .addCase(SaleSubscriptionApi.fulfilled, (state, action) => {
        if (!isLatestSaleCatalogRequest(state, action, "latestSubscriptionListRequestId")) return;
        const oldCurrentPage = state.isSubscription.current_page || "";
        const newCurrentPage = action.payload.current_page || "";
        const viewData = state.isSubscription && state.isSubscription.data;
        const newViewData = action.payload && action.payload.data;
        state.isSubscription = action.payload;
        if (oldCurrentPage && newCurrentPage && oldCurrentPage < newCurrentPage && oldCurrentPage !== newCurrentPage) {
          state.isSubscription.data = viewData && newViewData ? [...viewData, ...newViewData] : action.payload;
        }
        state.isApiStatus.SaleSubscriptionApi = 'succeeded';
      })
      .addCase(SaleSubscriptionApi.rejected, (state, action) => {
        if (!isLatestSaleCatalogRequest(state, action, "latestSubscriptionListRequestId")) return;
        state.isSubscription = [];
        state.isApiStatus.SaleSubscriptionApi = 'failed';
      })
      .addCase(SaleServiceToCartApi.pending, (state, action) => {
        registerDraftRequest(state, action);
        state.isApiStatus.SaleServiceToCartApi = 'loading';
      })
      .addCase(SaleServiceToCartApi.fulfilled, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        const { id, ...changes } = action.payload;
        const existingData = state.isCart.services.find((event) => event.id === id);
        if (existingData) {
          Object.keys(changes).map((keyName) => {
            existingData[keyName] = changes[keyName];
          });
        } else {
          const services = [...state.isCart.services, action.payload];
          state.isCart.services = services;
        }
        state.isApiStatus.SaleServiceToCartApi = 'succeeded';
      })
      .addCase(SaleServiceToCartApi.rejected, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isApiStatus.SaleServiceToCartApi = 'failed';
      })
      .addCase(SaleProductToCartApi.pending, (state, action) => {
        registerDraftRequest(state, action);
        state.isApiStatus.SaleProductToCartApi = 'loading';
      })
      .addCase(SaleProductToCartApi.fulfilled, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        const productPayload = normalizeSaleProduct(action.payload);
        const { id, ...changes } = productPayload;
        const existingData = state.isCart.products.find((event) => event.id === id);
        if (existingData) {
          Object.keys(changes).map((keyName) => {
            existingData[keyName] = changes[keyName];
          });
        } else {
          const products = [...state.isCart.products, productPayload];
          state.isCart.products = products;
        }
        state.isApiStatus.SaleProductToCartApi = 'succeeded';
      })
      .addCase(SaleProductToCartApi.rejected, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isApiStatus.SaleProductToCartApi = 'failed';
      })
      .addCase(SaleVoucherToCartApi.pending, (state, action) => {
        registerDraftRequest(state, action);
        state.isApiStatus.SaleVoucherToCartApi = 'loading';
      })
      .addCase(SaleVoucherToCartApi.fulfilled, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        const voucherPayload = normalizeSaleVoucher({
          ...action.payload,
          cost: normalizeCost(action?.meta?.arg?.cost) ?? normalizeCost(action?.payload?.cost) ?? action.payload?.amount,
        });
        const { id, ...changes } = voucherPayload;
        const existingData = state.isCart.vouchers.find((event) => event.id === id);
        if (existingData) {
          Object.keys(changes).map((keyName) => {
            existingData[keyName] = changes[keyName];
          });
        } else {
          const vouchers = [...state.isCart.vouchers, voucherPayload];
          state.isCart.vouchers = vouchers;
        }
        state.isApiStatus.SaleVoucherToCartApi = 'succeeded';
      })
      .addCase(SaleVoucherToCartApi.rejected, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isApiStatus.SaleVoucherToCartApi = 'failed';
      })
      .addCase(SaleMembershipToCartApi.pending, (state, action) => {
        registerDraftRequest(state, action);
        state.isApiStatus.SaleMembershipToCartApi = 'loading';
      })
      .addCase(SaleMembershipToCartApi.fulfilled, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        const membershipPayload = normalizeSaleMembership(action.payload);
        const { id, ...changes } = membershipPayload;
        const existingData = state.isCart.membership.find((event) => String(event.id) === String(id));
        if (existingData) {
          Object.keys(changes).map((keyName) => {
            existingData[keyName] = changes[keyName];
          });
        } else {
          const membership = [...state.isCart.membership, membershipPayload];
          state.isCart.membership = membership;
        }
        state.isApiStatus.SaleMembershipToCartApi = 'succeeded';
      })
      .addCase(SaleMembershipToCartApi.rejected, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isApiStatus.SaleMembershipToCartApi = 'failed';
      })
      .addCase(SaleSubscriptionToCartApi.pending, (state, action) => {
        registerDraftRequest(state, action);
        state.isApiStatus.SaleSubscriptionToCartApi = 'loading';
      })
      .addCase(SaleSubscriptionToCartApi.fulfilled, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        const subscriptionPayload = normalizeSaleSubscription(action.payload);
        const { id, ...changes } = subscriptionPayload;
        const existingData = state.isCart.subscription.find((event) => event.id === id);
        if (existingData) {
          Object.keys(changes).map((keyName) => {
            existingData[keyName] = changes[keyName];
          });
        } else {
          const subscription = [...state.isCart.subscription, subscriptionPayload];
          state.isCart.subscription = subscription;
        }
        state.isApiStatus.SaleSubscriptionToCartApi = 'succeeded';
      })
      .addCase(SaleSubscriptionToCartApi.rejected, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isApiStatus.SaleSubscriptionToCartApi = 'failed';
      })
      .addCase(ClientSuggetionListApi.pending, (state, action) => {
        registerDraftRequest(state, action);
        state.isApiStatus.ClientSuggetionListApi = 'loading';
      })
      .addCase(ClientSuggetionListApi.fulfilled, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        let old_current_page = state.isSuggetionListView.current_page ? state.isSuggetionListView.current_page : "";
        let new_current_page = action.payload.current_page ? action.payload.current_page : "";
        let viewdata = state.isSuggetionListView && state.isSuggetionListView.data;
        let newviewdata = action.payload && action.payload.data;
        state.isSuggetionListView = action.payload;
        if (old_current_page && new_current_page && old_current_page < new_current_page && old_current_page != new_current_page) {
          viewdata && newviewdata ? (state.isSuggetionListView.data = [...viewdata, ...newviewdata]) : action.payload;
        }
        state.isApiStatus.ClientSuggetionListApi = 'succeeded';
      })
      .addCase(ClientSuggetionListApi.rejected, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isSuggetionListView = [];
        state.isApiStatus.ClientSuggetionListApi = 'failed';
      })
      .addCase(SaleEmailInvoiceApi.pending, (state) => {
        state.isApiStatus.SaleEmailInvoiceApi = 'loading';
      })
      .addCase(SaleEmailInvoiceApi.fulfilled, (state) => {
        state.isApiStatus.SaleEmailInvoiceApi = 'succeeded';
      })
      .addCase(SaleEmailInvoiceApi.rejected, (state) => {
        state.isApiStatus.SaleEmailInvoiceApi = 'failed';
      })
      .addCase(VoucherApplyApi.pending, (state, action) => {
        registerDraftRequest(state, action);
        state.isApiStatus.VoucherApplyApi = 'loading';
      })
      .addCase(VoucherApplyApi.fulfilled, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isApiStatus.VoucherApplyApi = 'succeeded';
        state.isApplyVoucherCodeData = action.payload;
      })
      .addCase(VoucherApplyApi.rejected, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isApiStatus.VoucherApplyApi = 'failed';
      })
      .addCase(ReturnPaymentApi.pending, (state) => {
        state.isApiStatus.ReturnPaymentApi = 'loading';
      })
      .addCase(ReturnPaymentApi.fulfilled, (state) => {
        state.isApiStatus.ReturnPaymentApi = 'succeeded';
      })
      .addCase(ReturnPaymentApi.rejected, (state) => {
        state.isApiStatus.ReturnPaymentApi = 'failed';
      })
      .addCase(SendEmailVoucherApi.pending, (state) => {
        state.isApiStatus.SendEmailVoucherApi = 'loading';
      })
      .addCase(SendEmailVoucherApi.fulfilled, (state, action) => {
        state.isApiStatus.SendEmailVoucherApi = 'succeeded';
        state.isSendEmailVoucher = action.payload;
      })
      .addCase(SendEmailVoucherApi.rejected, (state) => {
        state.isApiStatus.SendEmailVoucherApi = 'failed';
        state.isSendEmailVoucher = "";
      })
      .addCase(SaleServicesManageApi.pending, (state, action) => {
        registerDraftRequest(state, action);
        state.isApiStatus.SaleServicesManageApi = 'loading';
      })
      .addCase(SaleServicesManageApi.fulfilled, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isApiStatus.SaleServicesManageApi = 'succeeded';
        state.isSaleServicesManage = action.payload;
      })
      .addCase(SaleServicesManageApi.rejected, (state, action) => {
        if (!consumeDraftRequest(state, action)) return;
        state.isSaleServicesManage = "";
        state.isApiStatus.SaleServicesManageApi = 'failed';
      })
      .addCase(SaleProductStaffDropdownApi.pending, (state) => {
        state.isApiStatus.SaleProductStaffDropdownApi = 'loading';
      })
      .addCase(SaleProductStaffDropdownApi.fulfilled, (state, action) => {
        state.isApiStatus.SaleProductStaffDropdownApi = 'succeeded';
        state.isSaleProductStaffDropdownList = action.payload;
      })
      .addCase(SaleProductStaffDropdownApi.rejected, (state) => {
        state.isSaleProductStaffDropdownList = [];
        state.isApiStatus.SaleProductStaffDropdownApi = 'failed';
      })
      .addCase(SaleSubscriptionStaffDropdownApi.pending, (state) => {
        state.isApiStatus.SaleSubscriptionStaffDropdownApi = 'loading';
      })
      .addCase(SaleSubscriptionStaffDropdownApi.fulfilled, (state, action) => {
        state.isApiStatus.SaleSubscriptionStaffDropdownApi = 'succeeded';
        state.isSaleSubscriptionStaffDropdownList = action.payload;
      })
      .addCase(SaleSubscriptionStaffDropdownApi.rejected, (state) => {
        state.isSaleSubscriptionStaffDropdownList = [];
        state.isApiStatus.SaleSubscriptionStaffDropdownApi = 'failed';
      });
  },
});
// Action creators are generated for each case reducer function
export const { RecoverPendingWalkInSale, ResolvedRecurringCheckout, reset, StartNewInvoice, InvoiceTabView, openAddSaleForm, closeAddSaleForm, openSaleDetailModal, closeSaleDetailModal, SaleTabView, SaleProductSearchName, SaleServiceSearchName, SaleServiceRemoveToCart, SaleProductRemoveToCart, AppointmentDetail, OpenClientSearchList, CloseClientSearchList, ClientSearchName, ClientSearchObj, SaleVoucherRemoveToCart, SaleMembershipRemoveToCart, OpenVoucherToForm, CloseVoucherToForm, VoucherToFormData, SaleOneOffVoucherToCartApi, SaleAppointmentToCartApi, SaleOneOffVoucherRemoveToCart, SaleCheckoutData, OpenCheckoutForm, CloseCheckoutForm, SaleCartUpdate, OpenSaleCompleted, CloseSaleCompleted, SaleCompletedData, OpenCardPaymentForm, CloseCardPaymentForm, CardPaymentData, OpenVoucherApplyForm, CloseVoucherApplyForm, SaleSubscriptionRemoveToCart, RemoveApplyVoucherCode, OpenSubscriptionCheckoutForm, RemoveSaleServicesManage } = saleSlice.actions;
export default saleSlice.reducer;
